// Clover Payment Form Handler
// Handles client-side credit card tokenization using Clover's iframe SDK

document.addEventListener('DOMContentLoaded', () => {
  // Locate the card section by its PAKMS-key element and walk up to the form it
  // lives in, rather than hard-coding a form id -- the checkout form is
  // #customer-payment-form but the donate form is #donation-form.
  const cardBody = document.querySelector('[data-pakms-key]');
  const form = cardBody?.closest('form');

  if (!form || !cardBody) {
    console.log('Clover form not found on this page');
    return;
  }

  const pakmsKey = cardBody.dataset.pakmsKey;

  if (!pakmsKey) {
    console.error('Clover PAKMS key not found');
    alert('Payment configuration error. Please contact support.');
    return;
  }

  console.log('Initializing Clover SDK...');

  // Initialize Clover SDK
  const clover = new Clover(pakmsKey);
  const elements = clover.elements();

  // Define styles to match existing form styling
  const styles = {
    body: {
      fontFamily: "'Open Sans', sans-serif",
      fontSize: '16px'
    },
    input: {
      fontSize: '16px',
      color: '#1f2937',
      '::placeholder': {
        color: '#9ca3af'
      }
    }
  };

  // Create card elements
  const cardNumber = elements.create('CARD_NUMBER', styles);
  const cardDate = elements.create('CARD_DATE', styles);
  const cardCvv = elements.create('CARD_CVV', styles);
  const cardPostalCode = elements.create('CARD_POSTAL_CODE', styles);

  // Mount elements to DOM
  cardNumber.mount('#clover-card-number');
  cardDate.mount('#clover-card-date');
  cardCvv.mount('#clover-card-cvv');
  cardPostalCode.mount('#clover-card-postal-code');

  console.log('Clover iframes mounted successfully');

  // Signal that the hosted-field iframes exist so payment-visibility.js can hide
  // the section (a promo-free order) without leaving them zero-sized. The flag
  // covers the case where this script's DOMContentLoaded handler runs before
  // payment-visibility.js has registered its listener (script order in <head>),
  // so the event alone would be missed; the event covers any later mount.
  window.cloverFieldsMounted = true;
  document.dispatchEvent(new Event('clover:mounted'));

  // Helper function to display errors
  const displayError = (elementId, error) => {
    const errorElement = document.getElementById(`${elementId}-errors`);
    if (errorElement) {
      errorElement.textContent = error || '';
    }
  };

  // Real-time validation - blur events
  cardNumber.addEventListener('blur', (event) => {
    if (event.error) {
      displayError('clover-card-number', event.error);
    }
  });

  cardDate.addEventListener('blur', (event) => {
    if (event.error) {
      displayError('clover-card-date', event.error);
    }
  });

  cardCvv.addEventListener('blur', (event) => {
    if (event.error) {
      displayError('clover-card-cvv', event.error);
    }
  });

  cardPostalCode.addEventListener('blur', (event) => {
    if (event.error) {
      displayError('clover-card-postal-code', event.error);
    }
  });

  // Clear errors on change
  cardNumber.addEventListener('change', (event) => {
    if (!event.error) {
      displayError('clover-card-number', null);
    }
  });

  cardDate.addEventListener('change', (event) => {
    if (!event.error) {
      displayError('clover-card-date', null);
    }
  });

  cardCvv.addEventListener('change', (event) => {
    if (!event.error) {
      displayError('clover-card-cvv', null);
    }
  });

  cardPostalCode.addEventListener('change', (event) => {
    if (!event.error) {
      displayError('clover-card-postal-code', null);
    }
  });

  // Handle form submission
  form.addEventListener('submit', function(event) {
    // A hidden card section means the order is free (e.g. a promo-covered ticket
    // with no donation), so there is nothing to tokenize -- let it submit as-is.
    const paymentSection = document.getElementById('payment-section');
    if (paymentSection && paymentSection.hidden) {
      return;
    }

    event.preventDefault();
    console.log('Form submission intercepted, tokenizing card...');

    const submitButton = document.getElementById('submit-button');
    const originalText = submitButton ? submitButton.textContent : '';
    const resetButton = () => {
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = originalText;
      }
    };
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = 'Processing...';
    }

    // Clear previous errors
    displayError('clover-card-number', null);
    displayError('clover-card-date', null);
    displayError('clover-card-cvv', null);
    displayError('clover-card-postal-code', null);

    // Create token
    clover.createToken()
      .then(function(result) {
        console.log('Token creation result:', result);

        if (result.errors) {
          // Display validation errors
          const errorFields = {
            'CARD_NUMBER': 'clover-card-number',
            'CARD_DATE': 'clover-card-date',
            'CARD_CVV': 'clover-card-cvv',
            'CARD_POSTAL_CODE': 'clover-card-postal-code'
          };

          Object.keys(result.errors).forEach((field) => {
            const error = result.errors[field];
            const elementId = errorFields[field];
            if (error.error && elementId) {
              displayError(elementId, error.error);
            }
          });

          resetButton();

          // Scroll to first error
          const firstError = document.querySelector('[id$="-errors"]:not(:empty)');
          if (firstError) {
            firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        } else if (result.token) {
          // Token created successfully
          console.log('Token created:', result.token);

          // Store token in hidden field
          document.getElementById('clover-token').value = result.token;

          // Extract card details for storage (brand and last4)
          // Note: Clover may not always provide these in the response
          // We'll send what we have and let the server handle it
          const cardDetails = {
            brand: result.card?.brand || 'Unknown',
            last4: result.card?.last4 || '****'
          };

          console.log('Card details:', cardDetails);
          document.getElementById('clover-card-details').value = JSON.stringify(cardDetails);

          // Submit the form
          console.log('Submitting form to server...');
          form.submit();
        } else {
          // Unexpected response
          console.error('Unexpected tokenization response:', result);
          alert('Payment processing error. Please try again.');
          resetButton();
        }
      })
      .catch(function(error) {
        console.error('Clover tokenization error:', error);
        alert('Payment processing error. Please try again or contact support.');

        resetButton();
      });
  });

  console.log('Clover payment handler initialized successfully');
});
